import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogProps,
  Box,
  IconButton,
  Typography,
  Tooltip,
  Fade,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
} from '@mui/icons-material';
import ButtonClose from './ButtonClose';

export interface DialogWindowProps extends Omit<
  DialogProps,
  'title' | 'fullScreen'
> {
  title?: string;
  onClose: () => void;
  children: React.ReactNode;
  disableClose?: boolean;
  hideExpand?: boolean;
  /** Some compact dialogs should retain their inset frame on small screens. */
  autoFullscreen?: boolean;
  /** Optional data-cy on the header close button. */
  closeDataCy?: string;
}

const DialogWindow: React.FC<DialogWindowProps> = ({
  open,
  onClose,
  title,
  children,
  disableClose = false,
  hideExpand = false,
  autoFullscreen = true,
  closeDataCy,
  ...otherDialogProps
}) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm')); // Below 600px
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isManualFullscreen, setIsManualFullscreen] = useState(false);

  // Auto-fullscreen on small screens
  useEffect(() => {
    if (open) {
      if (autoFullscreen && isSmallScreen && !isManualFullscreen) {
        setIsFullscreen(true);
      } else if (!isManualFullscreen) {
        setIsFullscreen(false);
      }
    }
  }, [autoFullscreen, isSmallScreen, open, isManualFullscreen]);

  const handleFullscreenToggle = () => {
    setIsFullscreen((prev) => {
      const newValue = !prev;
      setIsManualFullscreen(true); // Mark as manually toggled
      return newValue;
    });
  };

  // Reset manual flag when dialog closes
  useEffect(() => {
    if (!open) {
      setIsManualFullscreen(false);
    }
  }, [open]);

  const handleDialogClose = (
    _event: {},
    _reason: 'backdropClick' | 'escapeKeyDown'
  ) => {
    if (!disableClose) {
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      fullScreen={isFullscreen}
      disableEscapeKeyDown={disableClose}
      TransitionComponent={Fade}
      TransitionProps={{ timeout: 250 }}
      PaperProps={{
        sx: {
          overflow: 'hidden',
          animation: 'dialogEnter 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
          '@keyframes dialogEnter': {
            '0%': {
              opacity: 0,
              transform: 'scale(0.96) translateY(-8px)',
            },
            '100%': {
              opacity: 1,
              transform: 'scale(1) translateY(0)',
            },
          },
        },
      }}
      {...otherDialogProps}
    >
      {/* Custom Header */}
      <Box
        sx={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: 2,
          py: 1,
          minHeight: 64,
          bgcolor: 'studio.surfaces.fillSubtle',
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        {/* Left side: Close and Fullscreen buttons */}
        <Box
          sx={{
            position: 'absolute',
            left: 16,
            top: '50%',
            transform: 'translateY(-50%)',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <ButtonClose
            onClick={onClose}
            disabled={disableClose}
            dataCy={closeDataCy}
          />

          {!isSmallScreen && !hideExpand && (
            <Tooltip
              title={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
            >
              <IconButton
                onClick={handleFullscreenToggle}
                size="small"
                aria-label={
                  isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'
                }
                variant="outlined"
              >
                {isFullscreen ? (
                  <FullscreenExitIcon fontSize="small" />
                ) : (
                  <FullscreenIcon fontSize="small" />
                )}
              </IconButton>
            </Tooltip>
          )}
        </Box>

        {/* Center: Title */}
        {title && (
          <Typography
            variant="h6"
            sx={{
              fontSize: '1.5rem',
              fontWeight: 500,
              width: '100%',
              textAlign: 'center',
              px: { xs: 6, sm: 10 },
              lineHeight: 1.2,
            }}
          >
            {title}
          </Typography>
        )}
      </Box>

      {/* Children (DialogContent, DialogActions, etc.) */}
      {children}
    </Dialog>
  );
};

export default DialogWindow;
